import Table from "@/ui/table-div.tsx";
import TableBase from "@/ui/table";
import { Index, Pnl, PnlInPer, Value, WinRate } from "@/ui/formats.tsx";
import { IconComponent } from "@/ui/icon";
import { displayWalletAddress } from "@utils/wallet.ts";
import SolanaLogoImage from "../../ui/icon/svg/solana-logo.svg";

import {
  CopyTradeWallet,
  useCopyTradeWallets,
  SortColumn,
} from "@/hooks/use-copy-trade-data";
import { useWeb3Auth } from "@web3auth/modal-react-hooks";
import { useState } from "react";
import RawCopyTradeModal from "@/features/copy-trade-modal/raw-copy-trade-modal";
import { useWallet } from "@/providers/wallet";

const CopyTradeTable = ({
  isAllSelected,
  setIsAllSelected,
}: {
  isAllSelected: boolean;
  setIsAllSelected: (value: boolean) => void;
}) => {
  const [isCopyTradeModalOpen, setIsCopyTradeModalOpen] = useState(false);
  const [selectedWalletAddress, setSelectedWalletAddress] =
    useState<string>("");

  const { isConnected } = useWeb3Auth();
  const { setWallet } = useWallet();

  const { solanaAddresses, ethereumAddresses, currentWallet } = useWallet();

  const {
    wallets: filteredWallets,
    isLoading,
    isRefreshing,
    refreshWallets,
    sortConfig,
    handleSortToggle,
  } = useCopyTradeWallets(isAllSelected);

  const changeWalletOnWalletClick = async (addressToSet: string) => {
    const walletEth = ethereumAddresses.find(
      ({ address }) => address === addressToSet
    );
    if (walletEth) {
      if (walletEth.address !== currentWallet.address) {
        await setWallet(walletEth);
        refreshWallets();
      }
      setIsAllSelected(false);
      return;
    }

    const walletSol = solanaAddresses.find(
      ({ address }) => address === addressToSet
    );
    if (walletSol) {
      if (walletSol.address !== currentWallet.address) {
        await setWallet(walletSol);
        refreshWallets();
      }
      setIsAllSelected(false);
      return;
    }
  };

  // Helper to render sort arrows for columns
  const renderSortIcon = (column: SortColumn) => {
    if (sortConfig.column === column) {
      return (
        <IconComponent
          className="w-[0.375rem] h-[0.375rem]"
          name={sortConfig.direction === "desc" ? "arrowDown" : "arrowUp"}
          color="white"
        />
      );
    }
    return null;
  };

  return (
    <>
      <RawCopyTradeModal
        isOpen={isCopyTradeModalOpen}
        setIsOpen={() => setIsCopyTradeModalOpen(false)}
        walletAddress={selectedWalletAddress}
      />
      <Table className="h-[calc(100vh-7rem)] text-left">
        <Table.Header className="h-[1.5625rem] bg-black items-center">
          <Table.HeaderColumn className="w-[2.4%] flex justify-center items-center">
            #
          </Table.HeaderColumn>
          <Table.HeaderColumn className="w-[15.205%]">
            Wallet
          </Table.HeaderColumn>
          <Table.HeaderColumn
            className="w-[9.155%] cursor-pointer flex items-center gap-1"
            onClick={handleSortToggle("invested")}
          >
            Invested
            <div className="w-[0.75rem] h-[0.75rem] flex items-center justify-start">
              {renderSortIcon("invested")}
            </div>
          </Table.HeaderColumn>
          <Table.HeaderColumn
            className="w-[9.155%] cursor-pointer flex items-center gap-1"
            onClick={handleSortToggle("trades")}
          >
            Trades
            <div className="w-[0.75rem] h-[0.75rem] flex items-center justify-start">
              {renderSortIcon("trades")}
            </div>
          </Table.HeaderColumn>
          <Table.HeaderColumn
            className="w-[9.155%] cursor-pointer flex items-center gap-1"
            onClick={handleSortToggle("winRate")}
          >
            Win Rate
            <div className="w-[0.75rem] h-[0.75rem] flex items-center justify-start">
              {renderSortIcon("winRate")}
            </div>
          </Table.HeaderColumn>
          <Table.HeaderColumn
            className="w-[9.155%] cursor-pointer flex items-center gap-1"
            onClick={handleSortToggle("realizedPNL")}
          >
            Realized PNL
            <div className="w-[0.75rem] h-[0.75rem] flex items-center justify-start">
              {renderSortIcon("realizedPNL")}
            </div>
          </Table.HeaderColumn>
          <Table.HeaderColumn
            className="w-[9.155%] cursor-pointer flex items-center gap-1"
            onClick={handleSortToggle("realizedROI")}
          >
            Realized ROI
            <div className="w-[0.75rem] h-[0.75rem] flex items-center justify-start">
              {renderSortIcon("realizedROI")}
            </div>
          </Table.HeaderColumn>
          <Table.HeaderColumn
            className="w-[9.155%] cursor-pointer flex items-center gap-1"
            onClick={handleSortToggle("unrealizedPNL")}
          >
            Unrealized PNL
            <div className="w-[0.75rem] h-[0.75rem] flex items-center justify-start">
              {renderSortIcon("unrealizedPNL")}
            </div>
          </Table.HeaderColumn>
          <Table.HeaderColumn
            className="w-[9.155%] cursor-pointer flex items-center gap-1"
            onClick={handleSortToggle("unrealizedROI")}
          >
            Unrealized ROI
            <div className="w-[0.75rem] h-[0.75rem] flex items-center justify-start">
              {renderSortIcon("unrealizedROI")}
            </div>
          </Table.HeaderColumn>
          <Table.HeaderColumn
            className="w-[9.155%] cursor-pointer flex items-center gap-1"
            onClick={handleSortToggle("netPNL")}
          >
            Net PNL
            <div className="w-[0.75rem] h-[0.75rem] flex items-center justify-start">
              {renderSortIcon("netPNL")}
            </div>
          </Table.HeaderColumn>
          <Table.HeaderColumn
            className="w-[9.155%] cursor-pointer flex items-center gap-1"
            onClick={handleSortToggle("netROI")}
          >
            Net ROI
            <div className="w-[0.75rem] h-[0.75rem] flex items-center justify-start">
              {renderSortIcon("netROI")}
            </div>
          </Table.HeaderColumn>
        </Table.Header>
        <Table.Body>
          {isLoading || isRefreshing ? (
            <Table.Row>
              <div className="w-full h-[0.0625rem] bg-black overflow-hidden">
                <div className="w-full h-full bg-[#5AC4BE] animate-[loading_1s_ease-in-out_infinite]" />
              </div>
            </Table.Row>
          ) : isConnected ? (
            filteredWallets?.map((item: CopyTradeWallet, index: number) => {
              return (
                <Table.Row
                  onClick={async () => {
                    await changeWalletOnWalletClick(
                      item?.sourceWallet?.address || ""
                    );
                    setSelectedWalletAddress(item.address);
                    setTimeout(() => {
                      setIsCopyTradeModalOpen(true);
                    }, 200);
                  }}
                  key={`row-${item.address}-${item?.sourceWallet?.address}`}
                  className="h-[2rem] relative gradient-box before:py-[1px]
            before:pl-[1px] before:hover:bg-gradient-100 hover:bg-table-hover
            text-xs z-0 flex items-center"
                >
                  <Table.Column className="w-[2.4%] flex justify-center items-center font-semibold">
                    <Index value={index + 1} />
                  </Table.Column>
                  <Table.Column className="w-[15.205%] flex items-center font-semibold">
                    <div className="flex justify-start items-center gap-[0.3125rem]">
                      <div className="flex items-center rounded-full justify-center w-[1.1875rem] h-[1.1875rem] bg-black">
                        <div
                          className={`w-[58.86%] h-[58.86%] rounded-full ${item?.isActive ? "bg-green" : "bg-yellow-300"}`}
                        ></div>
                      </div>
                      <div className="w-[1.1875rem] h-[1.1875rem] z-0 rounded-full flex justify-center items-center bg-black">
                        <img
                          src={SolanaLogoImage}
                          alt="wallet-icon"
                          className="w-[1.1875rem] h-[1.1875rem] rounded-full"
                        />
                      </div>
                      {displayWalletAddress(item.address || "")}
                    </div>
                  </Table.Column>
                  <Table.Column className="w-[9.155%]">
                    <Value value={item.invested || 0} withSign={false} />
                  </Table.Column>
                  <Table.Column className="w-[9.155%]">
                    {item.trades || 0}
                  </Table.Column>
                  <Table.Column className="w-[9.155%]">
                    <WinRate value={item.winRate} />
                  </Table.Column>
                  <Table.Column className="w-[9.155%]">
                    <Pnl value={item.realizedPNL.amount} />
                  </Table.Column>
                  <Table.Column className="w-[9.155%]">
                    <PnlInPer value={item.realizedPNL.percentage} />
                  </Table.Column>
                  <Table.Column className="w-[9.155%]">
                    <Pnl value={item.unrealizedPNL.amount} />
                  </Table.Column>
                  <Table.Column className="w-[9.155%]">
                    <PnlInPer value={item.unrealizedPNL.percentage} />
                  </Table.Column>
                  <Table.Column className="w-[9.155%]">
                    <Pnl value={item.netPNL.amount} />
                  </Table.Column>
                  <Table.Column className="w-[9.155%]">
                    <PnlInPer value={item.netPNL.percentage} />
                  </Table.Column>
                </Table.Row>
              );
            })
          ) : (
            <TableBase.Row>
              <TableBase.Column className="py-[0.425rem] px-[2.1rem] text-xs">
                Sign in to view data
              </TableBase.Column>
            </TableBase.Row>
          )}
        </Table.Body>
      </Table>
    </>
  );
};

export default CopyTradeTable;
